import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, type View as RNView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { Text } from "@src/components/Text";

export interface SiteGroup {
  siteId: string;
  siteName: string;
}

interface SiteDropdownProps {
  siteGroups: SiteGroup[];
  selectedSiteId: string | null;
  selectedSiteName: string | null;
  onSelectSite: (siteId: string | null) => void;
}

export function SiteDropdown({ siteGroups, selectedSiteId, selectedSiteName, onSelectSite }: SiteDropdownProps) {
  const { theme } = useUnistyles();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<RNView>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const handleSelect = useCallback(
    (siteId: string | null) => {
      onSelectSite(siteId);
      setIsOpen(false);
    },
    [onSelectSite],
  );

  return (
    <View ref={containerRef} style={{ position: "relative", zIndex: 100 }}>
      <Pressable onPress={() => setIsOpen((prev) => !prev)}>
        <View p="xl" borderRadius={theme.spacing.md} bg="surfaceCard" borderWidth={1} borderColor="borderCard">
          <View flexDirection="row" justifyContent="space-between" alignItems="center">
            <View flexDirection="row" alignItems="center" gap="xs">
              <Icon name="building" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
              <Text textStyle="sm/regular" color="textSecondary">
                {selectedSiteName ?? "All sites"}
              </Text>
            </View>
            <Icon
              name={isOpen ? "chevronUp" : "chevronDown"}
              size={theme.spacing["5xl"]}
              color={theme.colors.textSecondary}
            />
          </View>
        </View>
      </Pressable>

      {isOpen && (
        <View
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            marginTop: theme.spacing.xs,
            maxHeight: 200,
            zIndex: 1000,
            backgroundColor: theme.colors.background,
            borderWidth: 1,
            borderColor: theme.colors.borderCard,
            borderRadius: theme.spacing.md,
            ...theme.shadows.md,
          }}
        >
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: theme.spacing.sm }}>
            <Pressable onPress={() => handleSelect(null)}>
              <View
                p="lg"
                borderRadius={theme.spacing.sm}
                style={
                  selectedSiteId === null ?
                    { backgroundColor: theme.colors.interactive.controls.selected_midground }
                  : undefined
                }
              >
                <Text textStyle="sm/regular" color={selectedSiteId === null ? "text" : "textSecondary"}>
                  All sites
                </Text>
              </View>
            </Pressable>
            {siteGroups.map((group) => (
              <Pressable key={group.siteId} onPress={() => handleSelect(group.siteId)}>
                <View
                  p="lg"
                  borderRadius={theme.spacing.sm}
                  style={
                    selectedSiteId === group.siteId ?
                      { backgroundColor: theme.colors.interactive.controls.selected_midground }
                    : undefined
                  }
                >
                  <Text textStyle="sm/regular" color={selectedSiteId === group.siteId ? "text" : "textSecondary"}>
                    {group.siteName}
                  </Text>
                </View>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}
